import React, { useState, useEffect, useRef } from 'react';
import { Box, Card, CardContent, Grid, Typography, TextField, Button, Avatar, Alert, Chip, IconButton, CircularProgress } from '@mui/material';
import { Person, Save, CameraAlt } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { employeesAPI, attendanceAPI, authAPI } from '../services/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5076/api';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({ first_name: '', last_name: '', phone: '', department: '', designation: '', address: '' });
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        department: user.department || '',
        designation: user.designation || '',
        address: user.address || '',
      });
    }
    loadStats();
  }, [user]);

  const loadStats = async () => {
    const now = new Date();
    try {
      const response = await attendanceAPI.getStats({ month: now.getMonth() + 1, year: now.getFullYear() });
      if (response.data.success) setStats(response.data.data);
    } catch (error) { console.error('Error:', error); }
  };

  const handleUpdate = async () => {
    setError('');
    try {
      await employeesAPI.update(user.id, formData);
      updateUser(formData);
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    }
  };

  const handlePasswordChange = async () => {
    setError('');
    try {
      await authAPI.changePassword(passwordData);
      setSuccess('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('employee_id', user.id);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/employees/upload-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        updateUser({
          ...user,
          profile_photo: response.data.data.profile_photo,
          profile_image: response.data.data.profile_image,
          face_verified: response.data.data.face_verified,
          descriptor_updated_at: response.data.data.descriptor_updated_at,
        });
        setSuccess('Profile photo updated successfully!');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const getProfilePhotoUrl = () => {
    if (user?.profile_photo) {
      return `${API_URL.replace('/api', '')}${user.profile_photo}`;
    }
    return null;
  };

  return (
    <Box sx={{ bgcolor: '#0A1929', p: 0, minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 3, 
            fontWeight: 600, 
            color: '#D4AF37',
            borderBottom: '2px solid #D4AF37',
            display: 'inline-block',
            pb: 1
          }}
        >
          My Profile
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ 
              textAlign: 'center', 
              bgcolor: '#1a3a2a', 
              border: '1px solid #D4AF37',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              <CardContent>
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <Avatar 
                    src={getProfilePhotoUrl()}
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      mx: 'auto', 
                      mb: 2, 
                      fontSize: 48, 
                      bgcolor: '#D4AF37',
                      color: '#1a3a2a',
                      border: '3px solid #D4AF37',
                    }}
                  >
                    {!getProfilePhotoUrl() && `${user?.first_name?.[0]}${user?.last_name?.[0]}`}
                  </Avatar>
                  <IconButton
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      bgcolor: '#D4AF37',
                      color: '#1a3a2a',
                      '&:hover': { bgcolor: '#B8860B' },
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? <CircularProgress size={20} sx={{ color: '#1a3a2a' }} /> : <CameraAlt />}
                  </IconButton>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handlePhotoUpload}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden-input"
                  />
                </Box>
                <Typography variant="caption" sx={{ display: 'block', color: '#D4AF37' }}>
                  Click camera icon to upload photo
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#D4AF37', mt: 1 }}>
                  {user?.first_name} {user?.last_name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#e0e0e0', mb: 1 }}>
                  {user?.email}
                </Typography>
                <Chip 
                  label={user?.role} 
                  sx={{ 
                    mb: 2, 
                    bgcolor: '#D4AF37', 
                    color: '#1a3a2a',
                    fontWeight: 600,
                    '& .MuiChip-label': { fontWeight: 600 }
                  }} 
                />
                <Typography variant="body2" sx={{ color: '#e0e0e0' }}>
                  Employee ID: {user?.employee_id}
                </Typography>
                {user?.shift && (
                  <Box sx={{ 
                    mt: 2, 
                    p: 2, 
                    bgcolor: '#0f2a1d', 
                    borderRadius: 2,
                    border: '1px solid #D4AF37'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#D4AF37' }}>
                      {user.shift.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#e0e0e0' }}>
                      {user.shift.start_time} - {user.shift.end_time}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ 
              mb: 3, 
              bgcolor: '#1a3a2a', 
              border: '1px solid #D4AF37',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#D4AF37' }}>
                  <Person sx={{ color: '#D4AF37' }} /> Personal Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      fullWidth 
                      label="First Name" 
                      value={formData.first_name} 
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#e0e0e0',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      fullWidth 
                      label="Last Name" 
                      value={formData.last_name} 
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#e0e0e0',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      fullWidth 
                      label="Phone" 
                      value={formData.phone} 
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#e0e0e0',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      fullWidth 
                      label="Department" 
                      value={formData.department} 
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#e0e0e0',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField 
                      fullWidth 
                      label="Designation" 
                      value={formData.designation} 
                      onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#e0e0e0',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField 
                      fullWidth 
                      label="Address" 
                      multiline 
                      rows={2} 
                      value={formData.address} 
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#e0e0e0',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
                <Button 
                  variant="contained" 
                  startIcon={<Save />} 
                  onClick={handleUpdate} 
                  sx={{ 
                    mt: 2, 
                    bgcolor: '#D4AF37', 
                    color: '#1a3a2a',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#B8860B' }
                  }}
                >
                  Save Changes
                </Button>
              </CardContent>
            </Card>

            <Card sx={{ 
              mb: 3, 
              bgcolor: '#1a3a2a', 
              border: '1px solid #D4AF37',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, color: '#D4AF37' }}>
                  Change Password
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField 
                      fullWidth 
                      label="Current Password" 
                      type="password" 
                      value={passwordData.current_password} 
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#e0e0e0',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField 
                      fullWidth 
                      label="New Password" 
                      type="password" 
                      value={passwordData.new_password} 
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      sx={{
                        '& .MuiInputLabel-root': { color: '#D4AF37' },
                        '& .MuiOutlinedInput-root': {
                          color: '#e0e0e0',
                          '& fieldset': { borderColor: '#D4AF37' },
                          '&:hover fieldset': { borderColor: '#D4AF37' },
                          '&.Mui-focused fieldset': { borderColor: '#D4AF37' }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
                <Button 
                  variant="outlined" 
                  onClick={handlePasswordChange} 
                  sx={{ 
                    mt: 2, 
                    borderColor: '#D4AF37', 
                    color: '#D4AF37',
                    '&:hover': { borderColor: '#B8860B', bgcolor: 'rgba(212, 175, 55, 0.1)' }
                  }}
                >
                  Change Password
                </Button>
              </CardContent>
            </Card>


          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Profile;
